/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps, RefObject, JSX } from 'preact';
import { useCallback, useRef, useEffect } from 'preact/hooks';

import { Floating } from '../UNSAFE_Floating';
import { Layer } from '../UNSAFE_Layer';
import { KEYS } from '../utils/PRIVATE_keyboardUtils';
import { type FocusableElement, focusWithin } from '../utils/PRIVATE_tabbableUtils';
import { usePopupAnimation } from '#UNSAFE_Popup/usePopupAnimation';
import { getTransformOrigin } from '#utils/PRIVATE_popupAnimationUtils';
import { AnimationPopupElement } from '../UNSAFE_Popup/usePopupAnimation';
import { CollectionInteractionContext } from '#hooks/UNSAFE_useCollectionInteractionContext';

import { dropdownStyles } from './themes/dropdownStyles.css';
import { useOutsideMousedown } from '../hooks/PRIVATE_useOutsideMousedown';
import {
  DURATION_LARGE,
  SCALE_UP_FULL,
  mergeAnimationStateObjects
} from '#utils/PRIVATE_animationUtils';

import { UnsafeDomHandle, UNSAFE_DOM_ACCESS } from '#utils/UNSAFE_unsafeDomAccess';
import { useEffectEvent } from '#hooks/PRIVATE_useEffectEvent';
import { getIsIosTablet } from '#UNSAFE_Menu/menuUtils';

type FloatingProps = ComponentProps<typeof Floating>;

//Same as Menu onClose, only difference is mouseDown instead of outside click
//Once menu consumes dropdown is going to start using mouseDown
export type CloseDetail = { reason: 'dismissed' } | { reason: 'outsideMousedown'; target: Element };

type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;

type Props = IntrinsicProps & {
  /**
   *Specifies whether Dropdown is open.
   * @default false
   */
  isOpen?: boolean;
  /**
   * Trigger element reference.
   */
  anchorRef: FloatingProps['anchorRef'];
  /**
   * Id of the dropdown container.
   * Used usually for accesibility reasons.
   */
  id?: string;
  /**
   * Called when dropdown is supposed to be closed.
   * Called when user tab/esc or mouseDown outside the dropdown.
   */
  onClose?: (detail: CloseDetail) => void;
  /**
   *Min width represented in pixels of the dropdown container.
   */
  minWidth?: number;
  /**
   * Placement of the floating element relative to its reference element.
   */
  placement?: FloatingProps['placement'];
  /**
   * Offset displaces the floating element from its core placement along the specified axes.
   */
  offset?: FloatingProps['offsetValue'];
  /**
   * Triggered when placement or coordinates are changed after collision is detected.
   */
  onPosition?: FloatingProps['onPosition'];
  /**
   * Specifies the flipping behavior along the respective axis.
   */
  flipOptions?: FloatingProps['flipOptions'];
  /**
   * Specifies the resizing behaviour for max-height and max-width of the floating component.
   */
  sizeOptions?: FloatingProps['sizeOptions'];

  /**
   *  Specifies the shift behavior along the respective axis
   */
  shiftOptions?: FloatingProps['shiftOptions'];
  /**
   * Specifies initial focus.
   * @default 'none'
   */
  initialFocus?: 'none' | 'firstFocusable';
};

const DEFAULT_PLACEMENT = 'bottom-start';
const DEFAULT_OFFSET = { mainAxis: 4, crossAxis: 0 };
const DEFAULT_SIZE_OPTIONS = { isMaxWidthAdjusted: true, isMaxHeightAdjusted: true };
const DEFAULT_FLIP_OPTIONS = { mainAxis: true, crossAxis: true };

const animationStates: Parameters<typeof usePopupAnimation>[0]['animationStates'] = {
  opening: (node: AnimationPopupElement) => {
    return mergeAnimationStateObjects(
      [
        SCALE_UP_FULL,
        {
          from: { transformOrigin: getTransformOrigin(node.placement, node.direction) },
          to: { transformOrigin: getTransformOrigin(node.placement, node.direction) }
        }
      ],
      {
        duration: DURATION_LARGE
      }
    );
  }
};

//TODO: Add animation to dropdown. JET-62628
//TODO: Investigate best approach to have different virtual padding depending on the position and implement it.JET-57950
export function Dropdown({
  isOpen = false,
  anchorRef,
  children,
  id,
  onClose,
  minWidth,
  placement = DEFAULT_PLACEMENT,
  offset = DEFAULT_OFFSET,
  onPosition,
  flipOptions = DEFAULT_FLIP_OPTIONS,
  sizeOptions = DEFAULT_SIZE_OPTIONS,
  shiftOptions,
  initialFocus = 'none'
}: Props) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const onTransitionEnd = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) { return }

      const dropdownElement = dropdownRef.current;
      if (!dropdownElement) { return }

      // honor firstFocusable initial focus behavior
      if (initialFocus === 'firstFocusable') {
        focusWithin(dropdownElement);
        return;
      }

      // JET-76442 + JET-76109: When VoiceOver is enabled on iOS tablets, we want the first menu item to receive focus.
      // This is to ensure that VoiceOver announces the menu items correctly and focus does not go to the next tabbable element.
      if (getIsIosTablet()) {
        const firstItem = dropdownElement.querySelector<FocusableElement>('[data-oj-menu-item-key]');
        if (firstItem) {
          focusWithin(firstItem);
        }
      }
    },
    [initialFocus]
  );
  const {
    status,
    setAnimationElementRef,
    onPosition: onPositionAnimated
  } = usePopupAnimation({
    isOpen,
    placement,
    isAnimatedOnMount: true,
    animationStates,
    onPosition,
    onTransitionEnd
  });

  const handleMousedownClose = useCallback(
    (event: MouseEvent) => {
      onClose?.({ reason: 'outsideMousedown', target: event.target as Element });
    },
    [onClose]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      switch (event.code) {
        case KEYS.ESC:
          onClose?.({ reason: 'dismissed' });
          break;
        case KEYS.TAB:
          onClose?.({ reason: 'dismissed' });
          break;
        default:
          break;
      }
    },
    [onClose]
  );

  // use the mousedown event to be consistent with legacy JET
  useOutsideMousedown({
    isDisabled: false,
    ref: [dropdownRef],
    handler: handleMousedownClose
  });

  // useEffectEvent ensures we always access the latest anchorRef.current value inside effects,
  // without having to include anchorRef in the dependency array and the eslint-disable-next comment
  const STABLE_getAnchor = useEffectEvent(() => anchorRef?.current);
  // Restore focus to anchor after Dropdown is closed
  useEffect(() => {
    const anchorElement = STABLE_getAnchor();
    if (
      !isOpen &&
      status === 'unmounted' &&
      anchorElement &&
      anchorElement instanceof HTMLElement
    ) {
      if (document.body.contains(anchorElement)) {
        anchorElement.focus();
      }
    }
  }, [STABLE_getAnchor, isOpen, status]);

  return (
    <>
      {status !== 'unmounted' ? (
        <Layer logicalParentRef={toLogicalParentRef(anchorRef)}>
          <Floating
            anchorRef={anchorRef}
            ref={setAnimationElementRef}
            placement={placement}
            flipOptions={flipOptions}
            shiftOptions={shiftOptions}
            sizeOptions={sizeOptions}
            offsetValue={offset}
            onPosition={onPositionAnimated}
            boundaryOptions={{ padding: { top: 8, bottom: 8, left: 0, right: 0 } }}>
            <div
              ref={dropdownRef}
              id={id}
              class={dropdownStyles.base}
              style={{ minWidth: `${minWidth || 0}px` }}
              onKeyDown={handleKeyDown}>
              <CollectionInteractionContext.Provider value={'embedded'}>
                {children}
              </CollectionInteractionContext.Provider>
            </div>
          </Floating>
        </Layer>
      ) : null}
    </>
  );
}

function toLogicalParentRef(anchorRef: FloatingProps['anchorRef']) {
  const current = anchorRef.current;

  if (current) {
    if (current instanceof Element) {
      return anchorRef as RefObject<Element>;
    } else if ((current as UnsafeDomHandle<Element>)[UNSAFE_DOM_ACCESS]) {
      return anchorRef as RefObject<UnsafeDomHandle<Element>>;
    }
  }

  return null;
}
